import { Observable, map } from 'rxjs';

import { Component, computed, effect, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { ApmTranslatePipe, AuthFacade } from '@mitsubishi/core';
import { UNIT_SYSTEM_OPTIONS, YES_NO_OPTIONS } from '@mitsubishi/shared/data-access';
import { ApmFieldErrorsComponent, InputDirective, SelectModule } from '@mitsubishi/shared/ui';
import { FormattingUtils } from '@mitsubishi/utils';

import { isNil } from '@angular-ru/cdk/utils';

import {
  COND_OUTLET_TYPES,
  CUSTOM_CONDITION,
  CUSTOM_CONDITION_KEY,
  EVAP_INLET_TYPES,
  STANDARD_CONDITION_RESOURCE_MAP,
} from '../../chiller-free-st.constants';
import { FreeStUtils } from '../../chiller-free-st.utils';
import {
  CsResponse,
  FreestFieldMappingDto,
  PowerSupply,
  Refrigerant,
  UomSystem,
} from '../../models/chiller-free-st.model';
import { ChillerFreeStService } from '../../services/chiller-free-st.service';
import { ChillerFreeStStore } from '../../stores/chiller-free-st.store';
import { ConvertibleInputComponent } from '../convertible-input/convertible-input.component';

@Component({
  selector: 'apm-chiller-free-st-configuration-filters',
  imports: [
    ReactiveFormsModule,
    SelectModule,
    ApmTranslatePipe,
    ConvertibleInputComponent,
    InputDirective,
    ApmFieldErrorsComponent,
  ],
  templateUrl: './chiller-free-st-configuration-filters.component.html',
  styleUrl: './chiller-free-st-configuration-filters.component.scss',
})
export class ChillerFreeStConfigurationFiltersComponent {
  #authFacade = inject(AuthFacade);
  #freeStService = inject(ChillerFreeStService);
  readonly store = inject(ChillerFreeStStore);

  readonly currentUserLanguage = this.#authFacade.userLanguage;

  readonly unitSystemOptions = UNIT_SYSTEM_OPTIONS;
  readonly yesNoOptions = YES_NO_OPTIONS;
  readonly evapInletTypes = [...EVAP_INLET_TYPES];
  readonly condOutletTypes = [...COND_OUTLET_TYPES];

  readonly standardConditions = computed(() => {
    const apiConditions = this.store.standardConditionList().map(sc => ({
      ...sc,
      resourceId: STANDARD_CONDITION_RESOURCE_MAP[sc.descKey] ?? 0,
    }));
    return [CUSTOM_CONDITION, ...apiConditions];
  });

  readonly isCustomised = computed(() => this.selectedCondition() === CUSTOM_CONDITION_KEY);

  readonly form = new FormGroup({
    uomSystem: new FormControl<string>('SI'),
    machineSeriesId: new FormControl<string | null>(null, Validators.required),
    compSeriesList: new FormControl<string[]>([], Validators.required),
    lowLift: new FormControl<boolean>(false),
    powerSupplyId: new FormControl<number | null>(null, Validators.required),
    circuitNum: new FormControl<number | null>(null, Validators.required),
    ref: new FormControl<string | null>(null, Validators.required),
    coolingCapacity: new FormControl<number | null>(null, Validators.required),
    standardConditionDesc: new FormControl<string | null>(null, Validators.required),
    accopRegionId: new FormControl<string | null>('beijing'),
    cop: new FormControl<number | null>(null),
    useEcon: new FormControl<boolean>(false),
    evapInletType: new FormControl<string>('EVA_INLET_TEMP'),
    evapInletValue: new FormControl<number | null>(null, Validators.required),
    evapOutletType: new FormControl<string>({ value: 'EVA_INLET_TEMP', disabled: true }),
    evapOutletTemp: new FormControl<number | null>(null, Validators.required),
    evapVarFlowPct: new FormControl<number | null>(null),
    evapFFId: new FormControl<number | null>(null),
    evapPD: new FormControl<number | null>(null),
    condInletType: new FormControl<string>({ value: 'COND_OUTLET_TEMP', disabled: true }),
    condInletTemp: new FormControl<number | null>(null, Validators.required),
    condOutletType: new FormControl<string>('COND_OUTLET_TEMP'),
    condOutletVal: new FormControl<number | null>(null, Validators.required),
    condVarFlowPct: new FormControl<number | null>(null),
    condFFId: new FormControl<number | null>(null),
    condPD: new FormControl<number | null>(null),
    selectionMode: new FormControl<string>('STD'),
  });

  readonly isFormValid = toSignal(this.form.statusChanges.pipe(map(() => this.form.valid)), { initialValue: false });

  readonly selectedCompSeriesNames = toSignal(this.form.controls.compSeriesList.valueChanges, {
    initialValue: [] as string[],
  });
  readonly selectedCondition = toSignal(this.form.controls.standardConditionDesc.valueChanges, {
    initialValue: null as string | null,
  });
  readonly selectedEvapInletType = toSignal(this.form.controls.evapInletType.valueChanges, {
    initialValue: 'EVA_INLET_TEMP' as string,
  });
  readonly selectedCondOutletType = toSignal(this.form.controls.condOutletType.valueChanges, {
    initialValue: 'COND_OUTLET_TEMP' as string,
  });

  readonly compressorSeriesOptions = computed(() => {
    return this.store.compressorSeriesList().map(cs => ({
      name: cs.compSeries.name,
      original: cs,
    }));
  });

  readonly selectedCompressors = computed<CsResponse[]>(() => {
    const all = this.store.compressorSeriesList();
    const selected = this.selectedCompSeriesNames() ?? [];
    return all.filter(cs => selected.includes(cs.compSeries.name));
  });

  readonly availablePowerSupplies = computed<PowerSupply[]>(() => {
    const selected = this.selectedCompressors();

    if (!selected.length) return [];
    return this.unionBy(
      selected.map(cs => cs.powerSupplySet),
      'id'
    ).sort((a, b) => a.desc.localeCompare(b.desc));
  });

  readonly availableCircuits = computed<number[]>(() => {
    const selected = this.selectedCompressors();
    if (!selected.length) return [];
    return this.unionNumbers(selected.map(cs => cs.circuitNumSet));
  });

  readonly availableRefrigerants = computed<Refrigerant[]>(() => {
    const selected = this.selectedCompressors();
    if (!selected.length) return [];
    return this.unionBy(
      selected.map(cs => cs.refSet),
      'code'
    );
  });

  readonly lowLiftEnabled = computed<boolean>(() => {
    const selected = this.selectedCompressors();
    return selected.length > 0 && selected.some(cs => cs.hasLowLift);
  });

  readonly showAccopRegion = computed<boolean>(() => {
    return this.selectedCondition() === 'gbt184301_idc';
  });

  readonly regionNameField = computed(() =>
    this.currentUserLanguage?.startsWith('zh') ? 'chineseName' : 'englishName'
  );

  readonly copComputeFn = (value: number, sourceUom: UomSystem): Observable<number> => {
    return this.#freeStService
      .compute({ uomSystem: sourceUom, computations: [{ field: 'cop', inputs: { value } }] })
      .pipe(map(res => res.results['cop']));
  };

  readonly fieldMappings = computed(() => {
    const fieldMap = this.store.fieldMappingMap();
    const evap = fieldMap['EVAPORATOR'] ?? {};
    const cond = fieldMap['CONDENSER'] ?? {};
    const cooling = fieldMap['COOLING'] ?? {};
    const evapInletType = this.selectedEvapInletType();
    const condOutletType = this.selectedCondOutletType();

    const sym = (m: FreestFieldMappingDto | undefined) => m?.forcedUom ?? m?.symbol ?? '';
    const entry = (m: FreestFieldMappingDto | undefined) => ({ mapping: m, symbol: sym(m) });

    return {
      coolingCapacity: entry(cooling['machineInfo.cap']),
      cop: entry(cooling['machineInfo.cop']),
      evapInletValue: entry(evapInletType === 'EVA_INLET_FLOW' ? evap['evapWC.flow'] : evap['evapWC.inletTemp']),
      evapOutletTemp: entry(evap['evapWC.outletTemp']),
      evapPD: entry(evap['evapWC.pressureDrop']),
      evapFlow: entry(evap['evapWC.flow']),
      evapFoulingFactor: entry(evap['evapWC.ffval']),
      condInletTemp: entry(cond['condWC.inletTemp']),
      condOutletVal: entry(condOutletType === 'COND_OUTLET_FLOW' ? cond['condWC.flow'] : cond['condWC.outletTemp']),
      condPD: entry(cond['condWC.pressureDrop']),
      condFlow: entry(cond['condWC.flow']),
      condFoulingFactor: entry(cond['condWC.ffval']),
    };
  });

  readonly convertedFoulingFactorValList = computed(() => {
    const list = this.store.foulingFactorValList();
    const siMapping = this.store.siFoulingFactorMapping();
    const currentMapping = this.fieldMappings().evapFoulingFactor.mapping;

    if (
      !siMapping ||
      !currentMapping ||
      isNil(siMapping.conversionParamA) ||
      isNil(siMapping.conversionParamB) ||
      isNil(currentMapping.conversionParamA) ||
      isNil(currentMapping.conversionParamB)
    ) {
      return list;
    }

    const formatting = FreeStUtils.getFormatting(currentMapping, this.store.uomSystem());

    return list.map(item => {
      const baseValue = FormattingUtils.toBaseValue(item.val, siMapping.conversionParamA, siMapping.conversionParamB);
      const displayValue = FormattingUtils.toDisplayValue(
        baseValue,
        currentMapping.conversionParamA,
        currentMapping.conversionParamB
      );
      return {
        ...item,
        val: FormattingUtils.formatOutputValue(formatting, displayValue) ?? String(item.val),
      };
    });
  });

  private readonly evapCondControls = [
    this.form.controls.evapInletType,
    this.form.controls.evapInletValue,
    this.form.controls.evapOutletTemp,
    this.form.controls.evapVarFlowPct,
    this.form.controls.evapFFId,
    this.form.controls.condInletTemp,
    this.form.controls.condOutletType,
    this.form.controls.condOutletVal,
    this.form.controls.condVarFlowPct,
    this.form.controls.condFFId,
  ];

  constructor() {
    effect(() => {
      const enabled = this.lowLiftEnabled();
      if (!enabled) {
        this.form.controls.lowLift.setValue(false, { emitEvent: false });
        this.form.controls.lowLift.disable({ emitEvent: false });
      } else {
        this.form.controls.lowLift.enable({ emitEvent: false });
      }
    });

    effect(() => {
      const customised = this.isCustomised();
      for (const control of this.evapCondControls) {
        if (customised) {
          control.enable({ emitEvent: false });
        } else {
          control.disable({ emitEvent: false });
        }
      }
    });

    this.form.controls.evapOutletTemp.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.form.controls.evapInletValue.updateValueAndValidity();
    });

    this.form.controls.condInletTemp.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.form.controls.condOutletVal.updateValueAndValidity();
    });
  }

  onEvapInletTypeChange(): void {
    this.updateFrontEndLimitValidators();
    this.form.controls.evapInletValue.updateValueAndValidity();
  }

  onCondOutletTypeChange(): void {
    this.updateFrontEndLimitValidators();
    this.form.controls.condOutletVal.updateValueAndValidity();
  }

  onSystemUnitChange(systemUnit: UomSystem): void {
    if (!systemUnit || systemUnit === this.store.uomSystem()) return;
    this.store.loadFieldMappings(systemUnit).subscribe(() => {
      const condition = this.selectedCondition();
      if (condition && condition !== CUSTOM_CONDITION_KEY) {
        this.applyConditionDefaults(condition);
      }
    });
  }

  onSeriesChange(seriesId: string): void {
    if (!seriesId) return;
    this.store.loadCompressorSeries(seriesId);
    this.resetCompressorDependents();
  }

  onCompressorSeriesChange(): void {
    const powers = this.availablePowerSupplies();
    if (
      this.form.controls.powerSupplyId.value !== null &&
      !powers.some(p => p.id === this.form.controls.powerSupplyId.value)
    ) {
      this.form.controls.powerSupplyId.reset(null);
    }
    const circuits = this.availableCircuits();
    if (this.form.controls.circuitNum.value !== null && !circuits.includes(this.form.controls.circuitNum.value!)) {
      this.form.controls.circuitNum.reset(null);
    }
    const refs = this.availableRefrigerants();
    if (this.form.controls.ref.value !== null && !refs.some(r => r.code === this.form.controls.ref.value)) {
      this.form.controls.ref.reset(null);
    }
  }

  onConditionChange(): void {
    const condition = this.form.controls.standardConditionDesc.value;
    if (condition && condition !== CUSTOM_CONDITION_KEY) {
      this.applyConditionDefaults(condition);
    }
    this.updateFrontEndLimitValidators();
  }

  private applyConditionDefaults(conditionKey: string): void {
    const condition = this.store.standardConditionList().find(sc => sc.descKey === conditionKey);
    if (!condition) return;

    this.form.controls.evapInletValue.setValue(condition.evapInletVal, { emitEvent: false });
    this.form.controls.evapOutletTemp.setValue(condition.evapOutletVal, { emitEvent: false });
    this.form.controls.condInletTemp.setValue(condition.condInletVal, { emitEvent: false });
    this.form.controls.condOutletVal.setValue(condition.condOutletVal, { emitEvent: false });

    this.form.controls.evapVarFlowPct.setValue(condition.evapVarFlowPct, { emitEvent: false });
    this.form.controls.condVarFlowPct.setValue(condition.condVarFlowPct, { emitEvent: false });
    this.form.controls.evapFFId.setValue(condition.evapFFId, { emitEvent: false });
    this.form.controls.condFFId.setValue(condition.condFFId, { emitEvent: false });

    this.form.controls.evapInletType.setValue(condition.evapInletType);
    this.form.controls.condOutletType.setValue(condition.condOutletType);

    this.updateFrontEndLimitValidators();
  }

  updateFrontEndLimitValidators(): void {
    const frontEndLimit = this.store.frontEndLimit();
    if (!frontEndLimit) return;

    FreeStUtils.applyFrontEndLimitValidators(
      {
        evapInletValue: this.form.controls.evapInletValue,
        evapOutletTemp: this.form.controls.evapOutletTemp,
        condInletTemp: this.form.controls.condInletTemp,
        condOutletVal: this.form.controls.condOutletVal,
        evapVarFlowPct: this.form.controls.evapVarFlowPct,
        condVarFlowPct: this.form.controls.condVarFlowPct,
      },
      frontEndLimit,
      {
        seriesId: this.form.controls.machineSeriesId.value,
        evapInletType: this.form.controls.evapInletType.value ?? 'EVA_INLET_TEMP',
        condOutletType: this.form.controls.condOutletType.value ?? 'COND_OUTLET_TEMP',
        enabled: this.form.controls.standardConditionDesc.value === CUSTOM_CONDITION_KEY,
      }
    );
  }

  private resetCompressorDependents(): void {
    this.form.controls.compSeriesList.reset([]);
    this.form.controls.lowLift.setValue(false, { emitEvent: false });
    this.form.controls.powerSupplyId.reset(null);
    this.form.controls.circuitNum.reset(null);
    this.form.controls.ref.reset(null);
  }

  private unionBy<T>(arrays: T[][], key: keyof T): T[] {
    const seen = new Set();
    const result: T[] = [];
    for (const arr of arrays) {
      for (const item of arr) {
        if (!seen.has(item[key])) {
          seen.add(item[key]);
          result.push(item);
        }
      }
    }
    return result;
  }

  private unionNumbers(arrays: number[][]): number[] {
    return [...new Set(arrays.flat())].sort((a, b) => a - b);
  }
}
